import React, { Component } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  getCurrentUserSession,
  refreshAuthSession,
  registerPushToken,
} from './actions/user'
import {
  createPushTokenRegistrar,
  subscribeNativePushToken,
} from './utils/nativePushToken'
import LoadingCard from './components/LoadingCard'
import { RouteElement } from './utils/routerCompat'

import Navigation from './containers/Navigation'
import Login from './containers/Auth/Login'
import Signup from './containers/Auth/Signup'
import ConfirmAccount from './containers/Auth/ConfirmAccount'
import ResendVerificationCode from './containers/Auth/ResendVerificationCode'
import ForgotPassword from './containers/Auth/ForgotPassword'
import ForgotUsername from './containers/Auth/ForgotUsername'
import SetNewPassword from './containers/Auth/SetNewPassword'

import MyJournal from './containers/MyJournal'
import WriteJournal from './containers/WriteJournal'
import JournalEntry from './containers/JournalEntry'
import PublicJournalEntry from './containers/PublicJournalEntry'
import EditJournal from './containers/EditJournal'
import Community from './containers/Community'
import Challenge from './containers/Challenge'
import Chat from './containers/Chat'
import ChatRoom from './containers/ChatRoom'
import Notification from './containers/Notification'
import Author from './containers/Author'
import Billing from './containers/Billing'
import Setting from './containers/Setting'
import CancelConfirm from './containers/CancelConfirm'
import NoMatch from './components/NoMatch'

import MemberLevel from './components/MemberLevel'
import Following from './containers/Following'
import Follower from './containers/Follower'
import LovedUsers from './containers/LovedUsers'
import Contact from './components/Contact'
import UserFeedback from './components/UserFeedback'

import MyBooks from './containers/MyBooks'
import BookJournals from './containers/BookJournals'

import { createNotificationSubscription } from './actions/notification'

import gnjFav from './assets/images/favicon.svg'

import ReactGA from 'react-ga4'
ReactGA.initialize('UA-37230984-1')
ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search })

const RequireAuth = ({ auth, children }) => {
  const location = useLocation()
  const loading = auth.isFetchingCurrentUserSession

  if (loading) return <LoadingCard />

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return children
}

class App extends Component {
  constructor(props) {
    super(props)
    this.unsubscribeNotificationSubscription = null
    // Push token from the mobile app, registered for the signed-in user (security sprint PR 11).
    // Created on each mount and disposed on unmount (safe under StrictMode remounts).
    this.pushTokenRegistrar = null
    this.unsubscribeNativePushToken = null
    // Dark mode
    let d = document.getElementsByTagName('HTML')[0],
      m = localStorage.getItem('darkmode')
    if (m === 'true') {
      d.classList.add('dark-mode')
      window.ReactNativeWebView &&
        window.ReactNativeWebView.postMessage('dark-mode')
    }
  }

  async componentDidMount() {
    const registrar = createPushTokenRegistrar(
      registerPushToken,
      undefined,
      undefined,
      { refreshSession: refreshAuthSession }
    )
    this.pushTokenRegistrar = registrar
    this.unsubscribeNativePushToken = subscribeNativePushToken((token) =>
      registrar.setToken(token)
    )

    // Get accountData and userData
    try {
      const session = await this.props.getCurrentUserSession()

      if (session) {
        this.unsubscribeNotificationSubscription =
          await this.props.createNotificationSubscription()
      }
    } catch (err) {
      console.log(err)
    }

    // GA event tracking
    document
      .querySelectorAll('a, button, select, .trackit')
      .forEach(function (elem) {
        elem.addEventListener(
          'click',
          function () {
            let myId = elem.id
            ReactGA.event({
              category: 'GoodnightJournal App', //TODO: Replace this with username
              action: 'click',
              label: myId,
            })
          },
          false
        )
      })
  }

  componentDidUpdate() {
    // A no-op unless the signed-in user (or the token) changed since the last registration.
    const { user } = this.props
    this.pushTokenRegistrar?.setUsername(
      user.isAuthenticated
        ? user.accountData?.idToken?.payload?.['cognito:username']
        : null
    )
  }

  componentWillUnmount() {
    this.pushTokenRegistrar?.dispose()
    this.pushTokenRegistrar = null
    if (this.unsubscribeNativePushToken) {
      this.unsubscribeNativePushToken()
      this.unsubscribeNativePushToken = null
    }
    if (this.unsubscribeNotificationSubscription) {
      this.unsubscribeNotificationSubscription()
      this.unsubscribeNotificationSubscription = null
    }
  }

  render() {
    const { user } = this.props

    if (user.isFetchingCurrentUserSession !== false) {
      return (
        <div>
          <img
            src={gnjFav}
            className="logo-loading"
            alt="Loading GoodnightJournal.com"
          />
        </div>
      )
    }

    return (
      <Router>
        <header className="main-header" id="main_header">
          <Navigation />
        </header>
        <div className="App">
          <Routes>
            <Route
              path="/login"
              element={<RouteElement component={Login} routePath="/login" />}
            />
            <Route
              path="/signup"
              element={<RouteElement component={Signup} routePath="/signup" />}
            />
            <Route
              path="/confirm-account"
              element={
                <RouteElement
                  component={ConfirmAccount}
                  routePath="/confirm-account"
                />
              }
            />
            <Route
              path="/resend-verification-code"
              element={
                <RouteElement
                  component={ResendVerificationCode}
                  routePath="/resend-verification-code"
                />
              }
            />
            <Route
              path="/forgot-password"
              element={
                <RouteElement
                  component={ForgotPassword}
                  routePath="/forgot-password"
                />
              }
            />
            <Route
              path="/forgot-username"
              element={
                <RouteElement
                  component={ForgotUsername}
                  routePath="/forgot-username"
                />
              }
            />
            <Route
              path="/set-new-password"
              element={
                <RouteElement
                  component={SetNewPassword}
                  routePath="/set-new-password"
                />
              }
            />
            <Route
              path="/community"
              element={
                <RouteElement component={Community} routePath="/community" />
              }
            />
            <Route
              path="/contact"
              element={<RouteElement component={Contact} routePath="/contact" />}
            />
            <Route
              path="/public-journal/:journal_uid"
              element={
                <RouteElement
                  component={PublicJournalEntry}
                  routePath="/public-journal/:journal_uid"
                />
              }
            />

            <Route
              path="/journal/:journal_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={JournalEntry}
                    routePath="/journal/:journal_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/author/:author_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Author}
                    routePath="/author/:author_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/level"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={MemberLevel} routePath="/level" />
                </RequireAuth>
              }
            />
            <Route
              path="/following/:author_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Following}
                    routePath="/following/:author_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/follower/:author_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Follower}
                    routePath="/follower/:author_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/loved/:category/:journalOrComment_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={LovedUsers}
                    routePath="/loved/:category/:journalOrComment_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={MyJournal} routePath="/" />
                </RequireAuth>
              }
            />
            <Route
              path="/write"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={WriteJournal} routePath="/write" />
                </RequireAuth>
              }
            />
            <Route
              path="/edit/:journal_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={EditJournal}
                    routePath="/edit/:journal_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/challenge"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={Challenge} routePath="/challenge" />
                </RequireAuth>
              }
            />
            <Route
              path="/chat"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={Chat} routePath="/chat" />
                </RequireAuth>
              }
            />
            <Route
              path="/chat/:chatRoom_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={ChatRoom}
                    routePath="/chat/:chatRoom_uid"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/notification"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Notification}
                    routePath="/notification"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/author/:author_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={Author} routePath="/author/:author_uid" />
                </RequireAuth>
              }
            />
            <Route
              path="/my-account/billing"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Billing}
                    routePath="/my-account/billing"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/my-account/setting"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={Setting}
                    routePath="/my-account/setting"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/my-account/cancel"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={CancelConfirm}
                    routePath="/my-account/cancel"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/feedback"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={UserFeedback}
                    routePath="/feedback"
                  />
                </RequireAuth>
              }
            />
            <Route
              path="/books"
              element={
                <RequireAuth auth={user}>
                  <RouteElement component={MyBooks} routePath="/books" />
                </RequireAuth>
              }
            />
            <Route
              path="/book/:book_uid"
              element={
                <RequireAuth auth={user}>
                  <RouteElement
                    component={BookJournals}
                    routePath="/book/:book_uid"
                  />
                </RequireAuth>
              }
            />

            <Route
              path="*"
              element={<RouteElement component={NoMatch} routePath="*" />}
            />
          </Routes>
        </div>
      </Router>
    )
  }
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      getCurrentUserSession,
      createNotificationSubscription,
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
